"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const certificates = [
  { 
    name: "Authorized Leap Ahead Distributor of Core Products", 
    issuedBy: "Lucas Indian Services Ltd.", 
    image: "/images/certificates/c1.jpeg" 
  },
  { 
    name: "Authorized Dealer for Standard & Sub-assemblies", 
    issuedBy: "Sona BLW Precision Forgings Ltd.", 
    image: "/images/certificates/c2.jpeg" 
  },
  { 
    name: "Authorized Distributor", 
    issuedBy: "Varroc Engineering Ltd.", 
    image: "/images/certificates/c3.jpeg" 
  },
  { 
    name: "Authorized Dealer for Standard & Sub-assemblies", 
    issuedBy: "Valeo India Service Ltd.", 
    image: "/images/certificates/c4.jpeg" 
  },
  { 
    name: "Authorized Dealer for Standard & Sub-assemblies", 
    issuedBy: "Bosch Ltd. (Auto Ancillary division)", 
    image: "/images/certificates/c5.jpeg" 
  },
];

const policies = [
  { title: "Product Offerings", description: "Jalaram Automotive provides a wide range of auto electrical goods, including but not limited to alternators, starters, lighting solutions, electrical components, and related accessories.\n\nAll products are sourced directly from authorized manufacturers or their distributors to ensure authenticity and quality." },
  { title: "Order Placement", description: "Orders can be placed via email, phone, or through our official website.\n\nCustomers are required to provide accurate product details, quantities, and delivery addresses to avoid delays or errors.\n\nMinimum order quantities (MOQs) may apply to certain products and will be communicated at the time of inquiry." },
  { title: "Pricing and Payment Terms", description: "Prices are subject to change without prior notice due to market fluctuations and currency exchange rates (for export orders).\n\nAccepted payment methods include bank transfers, letters of credit (LC), and other mutually agreed payment options.\n\nAll payments must be cleared before dispatch unless otherwise agreed upon in writing.\n\nFor export orders, international payment terms (e.g., FOB, CIF) will be discussed and confirmed during the order process." },
  { title: "Shipping and Delivery", description: "Domestic deliveries are handled by reputed logistics providers to ensure timely and safe delivery.\n\nExport shipments are carried out via air or sea freight, with proper documentation as per international trade regulations.\n\nDelivery timelines will be communicated at the time of order confirmation.\n\nJalaram Automotive is not liable for delays caused by unforeseen circumstances such as customs clearance, natural disasters, or third-party logistics issues." },
  { title: "Product Warranty", description: "All products supplied are covered under the respective manufacturer’s warranty policies.\n\nWarranty claims must be supported with original purchase documentation and proof of any manufacturing defects.\n\nThe warranty does not cover damages caused by improper usage, installation, or handling." },
  { title: "Returns and Refunds", description: "Returns are accepted only in cases of defective or incorrectly supplied goods.\n\nCustomers must report any issues within 7 days of receiving the product.\n\nReturned goods must be in original condition and packaging.\n\nRefunds or replacements will be processed after verifying the returned products." },
  { title: "Customer Support", description: "Our customer support team is available to assist with inquiries, order tracking, and after-sales service.\n\nSupport can be reached via email or phone during business hours." },
  { title: "Compliance", description: "Jalaram Automotive complies with all relevant domestic and international trade regulations.\n\nCustomers are responsible for ensuring compliance with import laws in their respective countries for export orders." },
  { title: "Limitation of Liability", description: "Jalaram Automotive’s liability is limited to the value of the goods supplied. The company is not responsible for any indirect or consequential damages arising from the use of its products." },
  { title: "Amendments to Policies", description: "Jalaram Automotive reserves the right to amend these terms and policies at any time. Updates will be communicated via official channels." },
  { title: "Governing Law", description: "All transactions and disputes are subject to the jurisdiction of the courts in Ahmedabad, Gujarat, India." }
];

const CertificatesPage = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="container mx-auto py-10 px-4">
      <h1 className="text-3xl font-bold text-center mb-8">Our Certifications</h1>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {certificates.map((certificate, index) => (
          <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
            <div className="relative w-full h-48 m-3">
              <Image 
                src={certificate.image} 
                alt={certificate.name} 
                layout="fill" 
                objectFit="contain" 
                className="rounded-t-lg"
              />
            </div>
            <CardHeader>
              <CardTitle>{certificate.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Issued By: <span className="font-medium">{certificate.issuedBy}</span>
              </p>
              <Badge variant="outline" className="mt-2 bottom-0">Verified</Badge>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="mt-12">
        <h2 className="text-2xl font-semibold mb-4">Policies</h2>
        <div className="space-y-4">
          {policies.map((policy, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <CardTitle className="text-lg font-bold">{policy.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {expandedIndex === index ? policy.description : `${policy.description.substring(0, 200)}...`}
                </p>
                <Button variant="link" className="mt-2" onClick={() => toggleExpand(index)}>
                  {expandedIndex === index ? "Read Less" : "Read More"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CertificatesPage;
